import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { GridColDef } from '@mui/x-data-grid';
import { MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import DashboardDataGrid from 'src/common/components/dataGrid/DashboardDataGrid';
import { actionsColumn } from 'src/common/components/dataGrid/dashboardGridColumns';
import { getParticipantDisplayAge } from 'src/helpers/registrationPrice';
import { IPendingClaimParticipant } from 'src/interfaces/PendingClaim';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

type Props = {
  participants: IPendingClaimParticipant[];
  embedded?: boolean;
  onAbandonParticipant: (participantId: string, e?: MouseEvent<unknown>) => void;
};

const PendingClaimParticipantsTable = ({
  participants,
  embedded = false,
  onAbandonParticipant,
}: Props) => {
  const { t } = useTranslation();
  const { activeEditionId, editionsById } = useSystemOptionsStore(s => s);

  const columns = useMemo(
    (): GridColDef<IPendingClaimParticipant>[] => [
      {
        field: 'name',
        headerName: t('form.name'),
        flex: 1,
        minWidth: 160,
        sortable: false,
        valueGetter: (_, row) => {
          const edition =
            activeEditionId && editionsById?.[activeEditionId]
              ? editionsById[activeEditionId]
              : null;
          const age = getParticipantDisplayAge(
            { dateOfBirth: row.dateOfBirth },
            edition?.year ?? null,
            edition?.date,
          );
          const fullName = `${row.lastName} ${row.firstName}`;
          return age != null ? `${fullName} (${age})` : fullName;
        },
      },
      {
        field: 'phoneNumber',
        headerName: t('form.phoneNo'),
        width: 140,
        sortable: false,
        valueGetter: (_, row) => row.phoneNumber || '-',
      },
      { field: 'email', headerName: t('form.email'), flex: 1, minWidth: 160, sortable: false },
      { field: 'city', headerName: t('form.city'), width: 120, sortable: false },
      actionsColumn<IPendingClaimParticipant>(
        t,
        'participants.optionsLabel',
        ({ row }) => (
          <Tooltip title={t('participants.claim.pending.abandonParticipant')}>
            <IconButton
              size="small"
              color="secondary"
              aria-label={t('participants.claim.pending.abandonParticipant')}
              onClick={e => onAbandonParticipant(row.id, e)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
        72,
      ),
    ],
    [activeEditionId, editionsById, onAbandonParticipant, t],
  );

  return (
    <DashboardDataGrid<IPendingClaimParticipant>
      aria-label={t('participants.claim.pending.tableLabel')}
      embedded={embedded}
      layout={embedded ? 'autoHeight' : 'virtualized'}
      rows={participants}
      columns={columns}
    />
  );
};

export default PendingClaimParticipantsTable;
